import React, { useCallback, useEffect } from "react";
import { Grid } from "@mui/material";
import { useForm } from "react-hook-form";
import { EditableReviewItem } from "../../../common/components/EditableReviewItem";
import SSNMask from "../../../common/components/mask/SSNMask";
import MobileNumberMask from "../../../common/components/mask/MobileNumberMask";
import AddressZipMask from "../../../common/components/mask/AddressZipMask";
import { gql, useMutation } from "@apollo/client";
import clientGraphql from "../../../../common/apollo-graphql";
import Swal from "sweetalert2";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import moment from "moment";

const normalizeDigits = (value) => (value == null ? "" : String(value)).replace(/\D/g, "");
const isNoSsn = (value) => typeof value === "string" && value.startsWith("no_ssn");
const normalizeDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "string") return value;
  if (typeof value?.toDate === "function") return value.toDate();
  return value;
};

const validationSchema = yup.object().shape({
  birthdate: yup
    .mixed()
    .test("valid-birthdate", "Birthdate is invalid.", (value) => {
      const normalized = normalizeDateValue(value);
      if (!normalized) return true;
      return moment(normalized).isValid();
    })
    .test("birthdate-not-future", "Birthdate must be today or a past date.", (value) => {
      const normalized = normalizeDateValue(value);
      if (!normalized) return true;
      return moment(normalized).isSameOrBefore(moment(), "day");
    }),
  ssn: yup
    .string()
    .nullable()
    .test("ssn-length", "SSN must have 9 digits", (value) => {
      if (!value || isNoSsn(value)) return true;
      return normalizeDigits(value).length === 9;
    }),
  phone: yup
    .string()
    .nullable()
    .test("phone-length", "Phone number must have 10 digits", (value) => {
      if (!value) return true;
      return normalizeDigits(value).length === 10;
    }),
});

const PatientInfoForm = ({ details, edit, referralUuid, setIsEditMode, refetch, setSaving }) => {
  const { control, handleSubmit, formState: { errors }, setValue, reset } = useForm({
    defaultValues: {...details},
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const SAVE_PATIENT_MUTATION = gql`
    mutation ($params: String!) {
      save_patient(params: $params)
    }
  `;

  const [savePatient] = useMutation(SAVE_PATIENT_MUTATION, {
    client: clientGraphql,
  });

  const getLabel = (custom) => custom.label;
  const getInputProps = useCallback(fieldName => {
    if (fieldName === 'ssn') {
      return {
        inputComponent: SSNMask,
      };
    } else if (fieldName === 'phone') {
      return { inputComponent: MobileNumberMask };
    } else if (fieldName === 'address_zip') {
      return {
        inputComponent: AddressZipMask,
      };
    } else {
      return null;
    }
  }, []);

  const handleRetrieve = (address) => setValue("full_address", address);

  const onSubmit = (data) => {
    setSaving(true);
    const renderedFields = [
      "id",
      "firstname",
      "lastname",
      "phone",
      "ssn",
      "mrn",
      "birthdate",
      "full_address",
    ];
    const protectedFields = ["firstname", "lastname", "ssn"];

    const normalizeValue = (value) => (value == null ? "" : value);
    const filteredData = {};
    const changedFields = [];

    renderedFields.forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(data, key)) {
        return;
      }
      const value = data[key];
      if (protectedFields.includes(key) && (value === "" || value == null)) {
        return;
      }
      const normalizedValue = normalizeValue(value);
      const normalizedOriginal = normalizeValue(details?.[key]);
      if (key === "id") {
        filteredData[key] = value;
        return;
      }
      if (normalizedValue !== normalizedOriginal) {
        filteredData[key] = normalizedValue;
        changedFields.push(key);
      }
    });

    filteredData.referral_uuid = referralUuid;
    const hasSsn = data['ssn'] !== "" && data['ssn'] != null ? true : !!details?.ssn;
    filteredData.is_public_patient = !hasSsn;

    if (changedFields.length === 0) {
      setSaving(false);
      setIsEditMode(false);
      return;
    }

    savePatient({
      variables: {
        params: JSON.stringify(filteredData),
      },
    }).then(() => {
      setIsEditMode(false);
      refetch();
      Swal.fire({
        icon: 'success',
        text: "Saved Patient Information.",
        showConfirmButton: true,
      })
    }).catch((error) => {
      console.error(error);
      Swal.fire("Failed to save patient information.");
    });
  };

  useEffect(() => {
    if (details) {
      reset(details);
    }
  }, [details, reset]);

  return (
    <form id="patient-info-form" onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={1}>
        {[
          { field: "firstname", label: "First Name", kind: "text" },
          { field: "lastname", label: "Last Name", kind: "text" },
          { field: "phone", label: "Phone", kind: "phone" },
          { field: "ssn", label: "SSN", kind: "text" },
          { field: "mrn", label: "MRN", kind: "text" },
          { field: "birthdate", label: "Birthdate", kind: "date" },
          { field: "full_address", label: "Address", kind: "address", handleRetrieve },
        ]
          .map(({ field, label, kind, handleRetrieve }) => (
            <EditableReviewItem
              key={field}
              edit={edit}
              custom={{ field, label, kind }}
              control={control}
              errors={errors}
              getLabel={getLabel}
              getInputProps={getInputProps}
              label={label}
              value={details[field]}
              handleRetrieve={handleRetrieve}
            />
          ))}
      </Grid>

      {edit && <button type="submit" style={{ display: "none" }} />}
    </form>
  );

};

export default PatientInfoForm;
